/**
 * InjectedDependencies - module resolution for bundled game code.
 * Provides CommonJS-style require() that resolves engine, three, and addon imports.
 */

import * as THREE from 'three';
// Import commonly used Three.js examples directly for reliable access
import * as TSL from 'three/tsl';
import * as WEBGPU from 'three/webgpu';

/**
 * Creates a lazy proxy for the ENGINE namespace.
 * This proxy accesses the ENGINE module at property access time (via require),
 * ensuring that circular dependencies have been resolved before any property is read.
 */
function createEngineProxy(): any {
  return new Proxy(
    {},
    {
      get(_target, prop: string | symbol) {
        // Access ENGINE through require at access time to get fully resolved module
        // This ensures circular dependencies are resolved before any property is read
        const ENGINE = require('../index.js');
        return ENGINE[prop];
      },
    }
  );
}

/**
 * Materialises a deterministic, plain-object snapshot of a live engine module.
 *
 * In environments where the engine is transpiled by Turbopack (editor dev mode), each
 * named export is a lazy getter over a potentially-circular module graph. Accessing those
 * getters mid-eval (inside `new Function(bundle)`) can hit bindings that are still in a
 * Temporal Dead Zone, causing `__TURBOPACK__imported__module__... is not defined` errors.
 *
 * By reading every export **once**, outside the eval, in a try/catch per key, we force all
 * lazy module initialisations to complete before game.js class bodies execute. The result is
 * a plain frozen object whose properties are simple data descriptors — no getters, no TDZ.
 *
 * Note: the snapshot is shallow. Sub-namespace re-exports (e.g. `export * as WorldCommands`)
 * keep their original reference, so their members remain accessible by game code. Only
 * top-level ENGINE.* symbols, which are what game class definitions use, are flattened.
 *
 * Registries (ClassRegistry, etc.) are held by reference, so game class registration
 * (decorators, registerSpecialization) still mutates the live engine state correctly.
 */
function materialiseEngineSnapshot(engineModule: object): object {
  const snapshot: Record<string | symbol, unknown> = {};
  for (const key of Reflect.ownKeys(engineModule)) {
    try {
      snapshot[key] = (engineModule as Record<string | symbol, unknown>)[key];
    } catch (err) {
      // A genuinely uninitialised binding — skip rather than abort the whole snapshot.
      console.warn(`[InjectedDependencies] Could not resolve engine export "${String(key)}" during snapshot:`, err);
    }
  }

  const frozen = Object.freeze(snapshot);
  return frozen;
}

/**
 * Creates an injected dependencies function for CommonJS module resolution.
 * This function resolves module imports for '@gnsx/genesys.js', 'three', and Three.js examples.
 *
 * When `engineModule` is provided (e.g. the editor passing its live ENGINE namespace),
 * the engine is snapshotted into a stable plain object before being handed to game.js eval,
 * eliminating Turbopack lazy-binding timing hazards for all eval sites without per-symbol warm-up.
 *
 * When omitted (node / play runtime where `require` already resolves cleanly), the lazy proxy
 * is used as before so circular dependencies are resolved at property-access time.
 *
 * @param engineModule - Optional engine module to use instead of the default import
 * @returns A function that takes a module name and returns the corresponding module
 */
export function createInjectedDependencies(engineModule?: any): (mod: string) => any {
  // When an explicit module is provided, materialise a deterministic snapshot so that
  // game.js eval never reads Turbopack lazy namespace getters during class definition.
  // When omitted, fall back to the lazy proxy for node/runtime paths where require() settles.
  const resolvedEngine = engineModule
    ? materialiseEngineSnapshot(engineModule as object)
    : createEngineProxy();

  return (mod: string) => {
    if (mod === '@gnsx/genesys.js') return resolvedEngine;
    if (mod === 'three') return THREE;
    if (mod === 'three/tsl') return TSL;
    if (mod === 'three/webgpu') return WEBGPU;

    throw new Error(`Unknown module: ${mod}. Supported modules: '@gnsx/genesys.js', 'three', 'three/tsl', 'three/webgpu'`);
  };
}
